import React, {useEffect, useState} from 'react';
import {Alert, Button, Form, Input, message, Modal, Select, Space, Tag, Tooltip} from 'antd';
import {PlusOutlined, SaveOutlined, CloseOutlined} from '@ant-design/icons';
import {createTermData} from '../api/termDataApi';
import {getDomainDataList} from '../api/domainDataApi';
import {getWordDataList} from '../api/wordDataApi';

const TermDataCreateModal = ({ isOpen, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [domainOptions, setDomainOptions] = useState([]);
  const [domainLoading, setDomainLoading] = useState(false);
  const [selectedDomainInfo, setSelectedDomainInfo] = useState(null);
  const [words, setWords] = useState([]);
  const [matchedWords, setMatchedWords] = useState([]);

  useEffect(() => {
    if (!isOpen) return;

    // 도메인 목록
    setDomainLoading(true);
    getDomainDataList()
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        const opts = (list || []).map((d) => {
          const domainType = d.domainType || d.dataType || d.domainTypeNm || '';
          return {
            value: d.domainDataId,
            label: `${d.domainNm ?? ''}${d.domainEngNm ? ` (${d.domainEngNm})` : ''}${domainType ? ` [${domainType}]` : ''}`.trim(),
            domainNm: d.domainNm,
            domainEngNm: d.domainEngNm,
            domainExpln: d.domainExpln,
            domainType: domainType,
          };
        });
        setDomainOptions(opts);
      })
      .catch((e) => {
        setError('도메인 목록을 불러오지 못했습니다: ' + (e?.response?.data?.message || e.message));
      })
      .finally(() => setDomainLoading(false));

    // 전역 단어 목록
    (async () => {
      try {
        const res = await getWordDataList();
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setWords(list || []);
      } catch {
        setWords([]);
      }
    })();
  }, [isOpen]);

  // 값 변경 시: 용어명 토큰 매칭 -> 자동 약어 채우기
  const handleValuesChange = (changed) => {
    if (Object.prototype.hasOwnProperty.call(changed, 'trmdNm')) {
      const termName = (changed.trmdNm ?? '').trim();
      const tokens = termName
        .split(/\s+/)
        .map((t) => t.trim())
        .filter((t) => t.length > 0);

      if (!tokens.length || !words.length) {
        setMatchedWords([]);
        return;
      }

      const matched = [];
      tokens.forEach((tk) => {
        const exact = words.find((w) => String(w.wordNm || '').trim() === tk);
        if (exact) { matched.push(exact); return; }
        const alt = words.find((w) => {
          const syn = (w.synm || '').toString();
          if (!syn) return false;
          const parts = syn.split(/[\s,/|]+/).map((s) => s.trim()).filter(Boolean);
          return parts.includes(tk);
        });
        if (alt) matched.push(alt);
      });

      setMatchedWords(matched);

      if (matched.length) {
        const abbr = matched
          .map((w) => (w.wordEngAbbrNm || '').toString().trim())
          .filter(Boolean)
          .join('_');
        if (abbr) form.setFieldsValue({ trmEngAbbrNm: abbr });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const values = await form.validateFields();
      setLoading(true);
      const payload = { ...values };
      // 용어명 공백 제거
      payload.trmdNm = (payload.trmdNm || '').replace(/\s+/g, '');
      // 도메인 미선택 시 필드 제거
      if (payload.domainDataId === '' || payload.domainDataId == null) {
        delete payload.domainDataId;
      }
      await createTermData(payload);
      message.success('용어가 성공적으로 생성되었습니다.');
      form.resetFields();
      setSelectedDomainInfo(null);
      setMatchedWords([]);
      onSuccess?.();
      onClose();
    } catch (error) {
      if (error?.errorFields) return; // 폼 검증 실패
      setError('용어 생성 중 오류가 발생했습니다: ' + (error?.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedDomainInfo(null);
    setMatchedWords([]);
    setError('');
    onClose();
  };

  const domainLabel = (
    <Space size={4}>
      <span>도메인</span>
    </Space>
  );

  return (
    <Modal
      title={
        <Space>
          <PlusOutlined />
          <span>용어 생성</span>
        </Space>
      }
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      maskClosable={false}
      forceRender
      width={900}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
        onValuesChange={handleValuesChange}
        initialValues={{ trmdNm: '', trmExpln: '', trmEngAbbrNm: '', domainDataId: '', synm: '' }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item
            name="trmdNm"
            label="용어명"
            rules={[{ required: true, message: '용어명을 입력해주세요' }]}
          >
            <Input placeholder="예: 봉사 활동 내용" />
          </Form.Item>

          <Form.Item
            name="trmEngAbbrNm"
            label="영문약어"
            rules={[]}
          >
            <Input placeholder="예: VOL_ACT_CONT" />
          </Form.Item>

          {/* 매칭된 단어 라벨 표시 */}
          <div style={{ gridColumn: '1 / span 2' }}>
            {matchedWords.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: '#555', fontSize: 12, paddingTop: 4 }}>매칭된 단어:</span>
                <Space size={[6, 6]} wrap>
                  {matchedWords.map((w, idx) => (
                    <Tooltip
                      key={`${w.wordNm || idx}-${idx}`}
                      placement="top"
                      title={
                        <div>
                          <div><strong>영문명:</strong> {w.wordEngNm || '-'}</div>
                          <div><strong>설명:</strong> {w.wordExpln || '-'}</div>
                        </div>
                      }
                    >
                      <Tag color="blue">
                        {w.wordNm || '-'} | {w.wordEngAbbrNm || '-'} | {w.wordEngNm || '-'} | {w.wordExpln || '-'}
                      </Tag>
                    </Tooltip>
                  ))}
                </Space>
              </div>
            )}
          </div>

          <Form.Item name="domainDataId" label={domainLabel}
            extra={selectedDomainInfo ? (
              <div style={{ marginTop: 6, maxWidth: '100%' }}>
                <Tag color="geekblue" style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'inline-block', maxWidth: '100%' }}>
                  {(selectedDomainInfo.domainNm || '-')}
                  {selectedDomainInfo.domainEngNm ? ` | ${selectedDomainInfo.domainEngNm}` : ' | -'}
                  {selectedDomainInfo.domainType ? ` | ${selectedDomainInfo.domainType}` : ' | -'}
                  {selectedDomainInfo.domainExpln ? ` | ${selectedDomainInfo.domainExpln}` : ' | -'}
                </Tag>
              </div>
            ) : null}
          >
            <Select
              showSearch
              placeholder="도메인명, 영문명, 설명, 데이터유형으로 검색하세요"
              options={domainOptions}
              loading={domainLoading}
              allowClear
              optionFilterProp="label"
              filterOption={(input, option) => {
                const label = (option?.label ?? '').toLowerCase();
                const value = (option?.value ?? '').toString().toLowerCase();
                const domainNm = (option?.domainNm ?? '').toLowerCase();
                const domainEngNm = (option?.domainEngNm ?? '').toLowerCase();
                const domainExpln = (option?.domainExpln ?? '').toLowerCase();
                const domainType = (option?.domainType ?? '').toLowerCase();
                const search = input.toLowerCase();
                return (
                  label.includes(search) ||
                  value.includes(search) ||
                  domainNm.includes(search) ||
                  domainEngNm.includes(search) ||
                  domainExpln.includes(search) ||
                  domainType.includes(search)
                );
              }}
              onChange={(value, option) => {
                if (!value) {
                  setSelectedDomainInfo(null);
                  return;
                }
                setSelectedDomainInfo({
                  domainNm: option?.domainNm || '',
                  domainEngNm: option?.domainEngNm || '',
                  domainType: option?.domainType || '',
                  domainExpln: option?.domainExpln || '',
                });
              }}
            />
          </Form.Item>

          <Form.Item
            name="synm"
            label="동의어"
          >
            <Input placeholder="동의어를 입력하세요" />
          </Form.Item>

          <Form.Item
            name="trmExpln"
            label="용어설명"
            rules={[{ required: true, message: '용어설명을 입력해주세요' }]}
            style={{ gridColumn: '1 / span 2' }}
          >
            <Input.TextArea
              placeholder="용어설명을 입력하세요"
              autoSize={{ minRows: 3, maxRows: 5 }}
            />
          </Form.Item>
        </div>

        {error && (
          <Alert message="오류" description={error} type="error" showIcon style={{ marginBottom: 16 }} />
        )}

        <div style={{ textAlign: 'right' }}>
          <Button icon={<CloseOutlined />} onClick={handleCancel} style={{ marginRight: 8 }}>
            취소
          </Button>
          <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSubmit}>
            저장
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default TermDataCreateModal;
